/**
 * Billing calls (Stripe). SAFE-BY-DEFAULT: the server reports `billingConfigured:false` and
 * `hasAccess:true` until Stripe keys are live, so the paywall never appears while billing is off.
 * All requests carry the signed-in user's token; the server + Stripe webhooks are the real enforcement.
 */
import axios from 'axios';
import { getAccessToken } from './authClient.ts';
import { apiBaseUrl } from './apiBase.ts';

/** Hosted-Checkout plan ids — see server/src/rest/billing/billingService.ts's CheckoutPlan. */
export type CheckoutPlan = 'consumer' | 'partner' | 'partner_client' | 'partner_overage';

export interface Entitlement {
  tenantId: string;
  /**
   * Whose plan this is. null = the tenant's as a whole — a consumer's own, or a firm-wide
   * complimentary grant. Set = one adviser's own plan inside a firm, covering their clients only.
   */
  userId: string | null;
  /** 'trial', 'comp' (free access), or a real Stripe plan id. */
  plan: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete';
  currentPeriodEnd: string | null;
}

export interface EntitlementResponse {
  billingConfigured: boolean;
  hasAccess: boolean;
  entitlement: Entitlement | null;
}

/**
 * ONE ADVISER'S client allowance, not the firm's. Since a plan belongs to a person, a colleague's
 * unused clients are not available to spend and there is no firm-wide total to show.
 */
export interface PartnerBillingSummary {
  mode: 'annual' | 'payg' | 'comp' | 'none';
  /** Clients included in the annual plan's base price. */
  includedBase: number;
  /** Total client slots available; null = unlimited (comp/free access). */
  capacity: number | null;
  used: number;
  /** null = unlimited. */
  remaining: number | null;
  canAddClient: boolean;
  /** What the firm would need to buy to add another client, if anything. */
  needs: 'partner_client' | 'partner_overage' | 'setup' | null;
}

export interface PartnerSummaryResponse {
  billingConfigured: boolean;
  summary: PartnerBillingSummary | null;
}

function getApiBaseUrl(): string {
  // One source for this — see services/apiBase.ts for why eight copies was a problem.
  return apiBaseUrl();
}

async function authHeaders(): Promise<{ Authorization: string; 'Content-Type': string }> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not signed in');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/**
 * { billingConfigured, hasAccess, entitlement }. Fails OPEN (hasAccess:true) on any error so a
 * transient hiccup never blocks a user — the server-side gate on the paid action is the backstop.
 */
export async function getEntitlement(tenantId?: string | null): Promise<EntitlementResponse> {
  try {
    const headers = await authHeaders();
    const url = `${getApiBaseUrl()}/rest/billing/entitlement${tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ''}`;
    const res = await axios.get(url, { headers, timeout: 30_000 });
    return res.data || { billingConfigured: false, hasAccess: true, entitlement: null };
  } catch {
    return { billingConfigured: false, hasAccess: true, entitlement: null };
  }
}

/**
 * A partner firm's client-billing picture: { billingConfigured, summary }, where summary is
 * { mode, capacity, used, remaining, canAddClient, needs } or null. Fails OPEN (billingConfigured
 * false → no gating) so a hiccup never blocks the portal; the server gate is the real backstop.
 */
export async function getPartnerSummary(tenantId?: string | null): Promise<PartnerSummaryResponse> {
  try {
    const headers = await authHeaders();
    const url = `${getApiBaseUrl()}/rest/billing/partner-summary${tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ''}`;
    const res = await axios.get(url, { headers, timeout: 30_000 });
    return res.data || { billingConfigured: false, summary: null };
  } catch {
    return { billingConfigured: false, summary: null };
  }
}

/**
 * Start hosted Checkout for a plan and redirect the browser to Stripe.
 *   'consumer'         — consumer 4-month pass
 *   'partner'          — firm annual subscription ($800/yr, 20 clients incl.)
 *   'partner_client'   — one pay-as-you-go client slot ($336)
 *   'partner_overage'  — one extra client beyond the annual 20 ($70)
 * Pass the firm's tenantId for partner plans (the caller may belong to several tenants).
 */
export async function startCheckout(plan: CheckoutPlan, tenantId?: string | null): Promise<{ ok: true } | { ok: false; message: string }> {
  const headers = await authHeaders();
  const body = tenantId ? { plan, tenantId } : { plan };
  const res = await axios.post(`${getApiBaseUrl()}/rest/billing/checkout`, body, { headers, timeout: 30_000 });
  if (res.data?.url) {
    window.location.assign(res.data.url);
    return { ok: true };
  }
  return { ok: false, message: 'Could not start the payment page. Please try again.' };
}

/** Open the Stripe customer portal (manage card / cancel / invoices) and redirect there. */
export async function openBillingPortal(): Promise<void> {
  const headers = await authHeaders();
  const res = await axios.post(`${getApiBaseUrl()}/rest/billing/portal`, {}, { headers, timeout: 30_000 });
  if (res.data?.url) window.location.assign(res.data.url);
}

/** Read ?billing=success|cancelled that Stripe appends on return, and strip it from the URL. */
export function consumeBillingReturn(): 'success' | 'cancelled' | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('billing');
    if (!outcome) return null;
    params.delete('billing');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    return outcome as 'success' | 'cancelled'; // 'success' | 'cancelled'
  } catch {
    return null;
  }
}
